import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";

export enum DialogType {
  DocumentAIAnalyzing = "DocumentAIAnalyzing",
  JobExportProgress = "JobExportProgress",
  SdsAnalyzing = "SdsAnalyzing",
}

export type DialogMode = "centered" | "minimizedInTray" | "expandedInTray";

export type MinimizedDialog = {
  id: string;
  dialogType: DialogType;
  props: Record<string, unknown>;
  mode: DialogMode;
  /** Called when the dialog is closed/dismissed via `close(id)`. */
  onDismiss?: () => void;
};
type CreateDialogProps = Omit<MinimizedDialog, "mode"> & {
  mode?: DialogMode;
};

interface MinimizedDialogActions {
  /**
   * Create a new dialog.
   * @param props - The properties for the dialog.
   */
  createDialog: (props: CreateDialogProps) => void;
  /**
   * Minimize a dialog.
   * @param id - The ID of the dialog to minimize.
   */
  minimize: (id: string) => void;
  /**
   * Expand a dialog in the tray.
   * @param id - The ID of the dialog to expand.
   */
  expand: (id: string) => void;
  /**
   * Restore a dialog to the center.
   * @param id - The ID of the dialog to restore.
   */
  restore: (id: string) => void;
  /**
   * Close a dialog.
   * @param id - The ID of the dialog to close.
   */
  close: (id: string) => void;
}

interface MinimizedDialogsContextType extends MinimizedDialogActions {
  dialogs: MinimizedDialog[];
}

// Split contexts: the actions object is memoized once (every action is an
// empty-dep useCallback, so it never changes identity), while the dialogs
// array is a new reference on every create/minimize/expand/restore/close.
// Consumers that only dispatch (e.g. useExportData's opt-in tray mode, which
// backs ~40 export call sites) subscribe to the actions context alone and
// therefore do NOT re-render when unrelated tray dialogs change state.
const MinimizedDialogActionsContext = createContext<MinimizedDialogActions | undefined>(undefined);
const MinimizedDialogsStateContext = createContext<MinimizedDialog[] | undefined>(undefined);

interface MinimizedDialogsProviderProps {
  children: ReactNode;
}

export function MinimizedDialogsProvider({ children }: MinimizedDialogsProviderProps) {
  const [dialogs, setDialogs] = useState<MinimizedDialog[]>([]);

  const createDialog = useCallback((props: CreateDialogProps) => {
    setDialogs((prev) => {
      const filtered = prev.filter((d) => d.id !== props.id);
      return [
        ...filtered,
        {
          ...props,
          mode: props.mode ?? "centered",
        },
      ];
    });
  }, []);

  const minimize = useCallback((id: string) => {
    setDialogs((prev) =>
      prev.map((dialog) => (dialog.id === id ? { ...dialog, mode: "minimizedInTray" } : dialog))
    );
  }, []);

  const expand = useCallback((id: string) => {
    setDialogs((prev) =>
      prev.map((dialog) => (dialog.id === id ? { ...dialog, mode: "expandedInTray" } : dialog))
    );
  }, []);

  const restore = useCallback((id: string) => {
    setDialogs((prev) =>
      prev.map((dialog) => (dialog.id === id ? { ...dialog, mode: "centered" } : dialog))
    );
  }, []);

  const close = useCallback((id: string) => {
    setDialogs((prev) => {
      const dialog = prev.find((d) => d.id === id);
      dialog?.onDismiss?.();
      return prev.filter((d) => d.id !== id);
    });
  }, []);

  const actions = useMemo(
    () => ({
      createDialog,
      minimize,
      expand,
      restore,
      close,
    }),
    [createDialog, minimize, expand, restore, close]
  );

  return (
    <MinimizedDialogActionsContext.Provider value={actions}>
      <MinimizedDialogsStateContext.Provider value={dialogs}>
        {children}
      </MinimizedDialogsStateContext.Provider>
    </MinimizedDialogActionsContext.Provider>
  );
}

/**
 * A hook to give access to the global minimized dialogs context.
 * ex. you can use this to create a new dialog from any component by calling `useMinimizedDialogs().createDialog(props)`.
 * Then, you can use the other methods to minimize, expand, restore, or close the dialog from anywhere else in the app.
 * This is great for long tasks, or dismissable/resumable forms.
 *
 * Note: this subscribes to the dialogs state, so the consumer re-renders on
 * every tray change. If you only need to dispatch (createDialog etc.), use
 * `useMinimizedDialogActionsOptional` instead.
 */
export function useMinimizedDialogs(): MinimizedDialogsContextType {
  const actions = useContext(MinimizedDialogActionsContext);
  const dialogs = useContext(MinimizedDialogsStateContext);
  const context = useMemo(() => {
    if (actions === undefined || dialogs === undefined) {
      return undefined;
    }
    return { dialogs, ...actions };
  }, [dialogs, actions]);
  if (context === undefined) {
    throw new Error("useMinimizedDialogs must be used within a MinimizedDialogsProvider");
  }
  return context;
}

/**
 * Actions-only access to the minimized-dialogs tray, for hooks/components
 * that dispatch but never read tray state. Two properties matter here (both
 * relied on by `useExportData`, which backs ~40 export call sites):
 *
 * - The returned object is memoized once by the provider and NEVER changes
 *   identity, so subscribing does not cause re-renders (or downstream
 *   useCallback churn) when unrelated tray dialogs open/close/minimize.
 * - Non-throwing: returns `undefined` instead of throwing when no
 *   `MinimizedDialogsProvider` is mounted (isolated unit tests, mainly —
 *   the provider wraps the whole app in App.tsx), so opt-in tray features
 *   degrade gracefully for consumers rendered outside it.
 */
export function useMinimizedDialogActionsOptional(): MinimizedDialogActions | undefined {
  return useContext(MinimizedDialogActionsContext);
}
